import {
    Box,
    Grid,
    Paper,
    Typography,
    Stack,
    Chip,
    IconButton,
    Tooltip,
    Divider,
    CircularProgress
} from '@mui/material';
import { useState } from 'react';
import dayjs from 'dayjs';
import { Visita } from '../consts/visitasTypes';
import { alpha } from '@mui/material/styles';
import AlarmOffIcon from '@mui/icons-material/AlarmOff';
import UndoIcon from '@mui/icons-material/Undo';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import UpcomingDeadlineIcon from '../../../components/Icons/UpcomingDeadlineIcon';
import TodayAgendaIcon from '../../../components/Icons/TodayAgendaIcon';
import { VisitaEstado } from '../consts/visitasStatus';
import { getOccurrenceLocationDisplayData } from './LugarOcurrenciaSeleccionado';
import { getDate } from '../../../utils/common';

type AgendaItem = Visita;

// "Próximas a vencer" solo debe listar visitas que el preventor todavía tiene que
// programar o realizar: las cerradas no corresponden y las en proceso ya las está
// realizando (SMP-582).
const ESTADOS_VISIBLES_VENCIMIENTO = [VisitaEstado.PENDIENTE, VisitaEstado.PROGRAMADA];

const MAX_PROXIMAS_A_VENCER = 6;

// Etiqueta de estado de "Próximas a vencer": mismos colores que las tarjetas de
// estado del Home (VisitIndicators).
const ESTADO_VENCIMIENTO_CHIP: Record<number, { label: string; sx: object }> = {
    [VisitaEstado.PENDIENTE]: {
        label: 'Pendiente',
        sx: { backgroundColor: '#F3F4F6', color: '#6B7280', border: '1px solid #6B7280' }
    },
    [VisitaEstado.PROGRAMADA]: {
        label: 'Programada',
        sx: { backgroundColor: '#FFF3C4', color: '#FFA000', border: '1px solid #FFA000' }
    }
};

export default function HomeAgenda({
    hoy = [],
    aVencer7 = [],
    aVencer30 = [],
    vencidas = [],
    devueltas = [],
    onProgramar,
    onRegistrar,
    isLoading = false
}: {
    hoy: AgendaItem[];
    aVencer7: AgendaItem[];
    aVencer30: AgendaItem[];
    vencidas?: AgendaItem[];
    devueltas?: AgendaItem[];
    onProgramar?: (item: AgendaItem) => void;
    onRegistrar?: (item: AgendaItem) => void;
    isLoading?: boolean;
}) {
    const calcularDiasVencimiento = (fechaVencimiento?: string) => {
        if (!fechaVencimiento) return null;

        const hoy = dayjs();
        const fecha = dayjs(fechaVencimiento);

        return fecha.diff(hoy, 'day');
    };

    // Devueltas: qué ítems tienen el motivo de devolución desplegado, por id de visita.
    const [motivosAbiertos, setMotivosAbiertos] = useState<Record<number, boolean>>({});
    const toggleMotivo = (id: number) =>
        setMotivosAbiertos((prev) => ({ ...prev, [id]: !prev[id] }));

    const getVencimientoChipSx = (dias: number) =>
        dias < 0
            ? { backgroundColor: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA' }
            : dias <= 3
            ? { backgroundColor: '#FFF7ED', color: '#C2410C', border: '1px solid #FDBA74' }
            : { backgroundColor: '#F0FDF4', color: '#15803D', border: '1px solid #86EFAC' };

    const renderItem = (
        item: AgendaItem,
        idx: number,
        esVencimiento: boolean = false,
        isLast: boolean = false,
        // Vencidas y Devueltas no dependen del estado de la visita: la acción
        // siempre es ir directo a la visita.
        accionForzada?: 'ir',
        esDevuelta: boolean = false
    ) => {
        const notaDevolucion = (item as any)?.notaDevolucion;

        // Las visitas ROAM (ligadas a un siniestro) no traen establecimiento:
        // el título y la dirección se completan con el lugar de ocurrencia.
        const lugarOcurrencia = (item?.siniestro as any)?.lugarOcurrencia;
        const occurrenceData = !item?.establecimiento
            ? getOccurrenceLocationDisplayData(
                  {
                      calle: lugarOcurrencia?.calle,
                      altura: lugarOcurrencia?.altura,
                      piso: lugarOcurrencia?.piso,
                      depto: lugarOcurrencia?.depto,
                      codigoPostal: lugarOcurrencia?.codigoPostal,
                      localidadData: lugarOcurrencia?.localidad
                  },
                  item?.siniestro?.nroRoam || item?.siniestro?.id
              )
            : null;

        // Algunas "Investigación AT" todavía no tienen el siniestro vinculado
        // (siniestro: null): ni establecimiento ni lugar de ocurrencia. Mostramos
        // al menos el tipo de actividad para poder identificar de qué se trata.
        const primeraActividad = item?.actividades?.[0]?.tipoVisita?.descripcion;

        const establecimiento =
            item?.establecimiento?.nombre ??
            (occurrenceData ? `Siniestro Nro ${occurrenceData.claimNumber}` : null) ??
            primeraActividad ??
            '-';

        const direccionRaw = item?.establecimiento?.direccion;

        const direccion = direccionRaw
            ? `${direccionRaw.calle ?? ''} ${direccionRaw.altura ?? ''} · ${
                  direccionRaw.localidad?.nombre ?? ''
              }${
                  direccionRaw.localidad?.provincia?.nombre
                      ? `, ${direccionRaw.localidad.provincia.nombre}`
                      : ''
              }`
            : occurrenceData
            ? `${occurrenceData.address} · ${occurrenceData.locality}, ${occurrenceData.province}`
            : primeraActividad
            ? `Visita #${item.id}`
            : '-';

        const dias = esVencimiento ? calcularDiasVencimiento(item.fechaLimite) : null;

        const textoVencimiento =
            dias !== null
                ? dias < 0
                    ? `Vencida hace ${Math.abs(dias)} días`
                    : dias === 0
                    ? `Vence hoy`
                    : `Vence en ${dias} días`
                : null;

        // En "Próximas a vencer" la acción depende del estado: una pendiente todavía
        // se programa, una ya programada se retoma para realizarla.
        const accion = accionForzada
            ? accionForzada
            : !esVencimiento
            ? 'registrar'
            : item.estado === VisitaEstado.PENDIENTE
            ? 'programar'
            : item.estado === VisitaEstado.PROGRAMADA
            ? 'ir'
            : null;

        // Estado y fecha programada: solo en "Próximas a vencer" (Vencidas y Devueltas
        // también son de vencimiento, pero con la acción forzada).
        const estadoChip =
            esVencimiento && !accionForzada ? ESTADO_VENCIMIENTO_CHIP[item.estado] : undefined;
        const fechaProgramada =
            estadoChip && item.estado === VisitaEstado.PROGRAMADA && item.fechaProgramada
                ? getDate(item.fechaProgramada, 'DD/MM')
                : null;

        const accionConfig = {
            registrar: { label: 'Registrar visita', handler: onRegistrar },
            programar: { label: 'Programar', handler: onProgramar },
            ir: { label: 'Ir a la visita', handler: onRegistrar }
        } as const;

        const motivoAbierto = !!motivosAbiertos[item.id];

        return (
            <Box
                key={idx}
                sx={{
                    py: 2,
                    px: 1,
                    mx: -1,
                    borderRadius: 1,
                    borderBottom: isLast ? 'none' : '1px solid #e0e0e0',
                    '&:hover': { backgroundColor: '#F9FAFB' }
                }}
            >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box minWidth={0}>
                        <Typography
                            fontWeight={600}
                            fontSize={14}
                            onClick={() => accion && accionConfig[accion].handler?.(item)}
                            sx={{
                                cursor: accion ? 'pointer' : 'default',
                                width: 'fit-content'
                            }}
                        >
                            {establecimiento}
                        </Typography>

                        <Typography variant="body2" fontSize={12} color="text.secondary">
                            {direccion}
                        </Typography>

                        {fechaProgramada && (
                            <Typography variant="body2" fontSize={12} color="text.secondary">
                                Programada para el {fechaProgramada}
                            </Typography>
                        )}
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center" ml={2} flexShrink={0}>
                        {estadoChip && (
                            <Chip
                                size="small"
                                label={estadoChip.label}
                                sx={{
                                    height: '20px',
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    ...estadoChip.sx,
                                    '& .MuiChip-label': { px: '6px' }
                                }}
                            />
                        )}

                        {textoVencimiento && dias !== null && (
                            <Chip
                                size="small"
                                icon={<AccessTimeIcon />}
                                label={textoVencimiento}
                                sx={{
                                    height: '20px',
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    ...getVencimientoChipSx(dias),
                                    '& .MuiChip-label': { px: '6px' },
                                    '& .MuiChip-icon': {
                                        color: 'inherit',
                                        fontSize: '12px',
                                        ml: '4px'
                                    }
                                }}
                            />
                        )}

                        {esDevuelta && (
                            <Tooltip title="Ver motivo de devolución">
                                <IconButton
                                    onClick={() => toggleMotivo(item.id)}
                                    sx={{
                                        color: 'warning.main',
                                        backgroundColor: motivoAbierto
                                            ? (theme) => alpha(theme.palette.warning.main, 0.12)
                                            : 'transparent'
                                    }}
                                >
                                    <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}

                        {accion && (
                            <Tooltip title={accionConfig[accion].label}>
                                <IconButton onClick={() => accionConfig[accion].handler?.(item)}>
                                    <ChevronRightIcon fontSize="medium" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                </Stack>

                {esDevuelta && motivoAbierto && (
                    <Box
                        sx={{
                            mt: 1,
                            p: 1.5,
                            backgroundColor: '#F8FAFC',
                            borderLeft: '3px solid #CBD5E1',
                            borderRadius: '0 6px 6px 0'
                        }}
                    >
                        <Typography sx={{ fontSize: 13, color: '#374151', lineHeight: 1.55 }}>
                            {notaDevolucion || 'No se registró un motivo de devolución.'}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    // La lista ocupa el 100% del espacio disponible dentro de la card (que ahora tiene un alto
    // minimo de 45vh via flexGrow en el Box padre): el scroll interno y la cantidad de registros
    // visibles se ajustan solos al alto real de la card en vez de quedar fijos a ~3 items, para
    // aprovechar pantallas grandes sin dejar espacio en blanco.
    const renderList = (content: React.ReactNode) => (
        <Box
            sx={{
                height: '100%',
                overflowY: 'auto',
                overflowX: 'hidden',
                pr: 0.5
            }}
        >
            {content}
        </Box>
    );

    const renderEmpty = <Typography color="text.secondary">No se encontraron visitas</Typography>;
    const isAgendaLoading = isLoading && hoy === null;
    const proximosAVencer = (() => {
        if (!aVencer7 || !aVencer30) return [];

        const esVisible = (item: AgendaItem) =>
            ESTADOS_VISIBLES_VENCIMIENTO.includes(item.estado);

        // aVencer30 repite las visitas que ya trae aVencer7 (30 días incluye a los 7):
        // se descartan por id para no duplicarlas en el listado.
        const idsVistos = new Set<number>();
        const visibles = [...aVencer7, ...aVencer30].filter((item) => {
            if (idsVistos.has(item.id) || !esVisible(item)) return false;
            idsVistos.add(item.id);
            return true;
        });

        // El back no ordena las listas: se ordena por vencimiento, la más próxima
        // primero y sin agrupar por estado (SMP-582).
        return visibles
            .sort(
                (a, b) =>
                    dayjs(a.fechaLimite).valueOf() - dayjs(b.fechaLimite).valueOf() || a.id - b.id
            )
            .slice(0, MAX_PROXIMAS_A_VENCER);
    })();

    return (
        <Grid container spacing={6}>
            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                <Paper
                    elevation={0}
                    sx={{
                        pt: '7px',
                        pl: 3,
                        pr: 3,
                        pb: 3,
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        backgroundColor: '#ffffff',
                        width: '100%',
                        minHeight: '45vh',
                        maxHeight: '45vh',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center" mb="5px">
                        <Box
                            sx={{
                                width: 42,
                                height: 42,
                                borderRadius: '50%',
                                backgroundColor: (theme) =>
                                    alpha(theme.palette.primary.light, 0.12),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <TodayAgendaIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                        </Box>
                        <Typography variant="h6" fontWeight={700}>
                            Agenda para hoy
                        </Typography>
                    </Stack>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                        {isAgendaLoading ? (
                            <Box display="flex" justifyContent="center" py={4}>
                                <CircularProgress size={32} />
                            </Box>
                        ) : !hoy || hoy.length === 0 ? (
                            renderEmpty
                        ) : (
                            renderList(hoy.map((v, i) => renderItem(v, i, false)))
                        )}
                    </Box>
                </Paper>
            </Grid>

            <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                <Paper
                    elevation={0}
                    sx={{
                        pt: '7px',
                        pl: 3,
                        pr: 3,
                        pb: 3,
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        backgroundColor: '#ffffff',
                        width: '100%',
                        minHeight: '45vh',
                        maxHeight: '45vh',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center" mb="5px">
                        <Box
                            sx={{
                                width: 42,
                                height: 42,
                                borderRadius: '50%',
                                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.12),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <UpcomingDeadlineIcon sx={{ color: 'error.main', fontSize: 24 }} />
                        </Box>
                        <Typography variant="h6" fontWeight={700}>
                            Próximas a vencer
                        </Typography>
                    </Stack>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                        {isAgendaLoading ? (
                            <Box display="flex" justifyContent="center" py={4}>
                                <CircularProgress size={32} />
                            </Box>
                        ) : proximosAVencer.length === 0 ? (
                            renderEmpty
                        ) : (
                            renderList(proximosAVencer.map((v, i) => renderItem(v, i, true)))
                        )}
                    </Box>
                </Paper>
            </Grid>

            <Grid item xs={12} md={6} sx={{ pt: '24px !important', display: 'flex' }}>
                <Paper
                    elevation={0}
                    sx={{
                        pt: '7px',
                        pl: 3,
                        pr: 3,
                        pb: 3,
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        backgroundColor: '#ffffff',
                        width: '100%',
                        minHeight: '45vh',
                        maxHeight: '45vh',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center" mb="5px">
                        <Box
                            sx={{
                                width: 42,
                                height: 42,
                                borderRadius: '50%',
                                backgroundColor: (theme) => alpha(theme.palette.error.main, 0.12),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <AlarmOffIcon sx={{ color: 'error.main', fontSize: 24 }} />
                        </Box>
                        <Typography variant="h6" fontWeight={700}>
                            Vencidas
                        </Typography>
                    </Stack>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                        {isAgendaLoading ? (
                            <Box display="flex" justifyContent="center" py={4}>
                                <CircularProgress size={32} />
                            </Box>
                        ) : !vencidas || vencidas.length === 0 ? (
                            renderEmpty
                        ) : (
                            renderList(vencidas.map((v, i) => renderItem(v, i, true, false, 'ir')))
                        )}
                    </Box>
                </Paper>
            </Grid>

            <Grid item xs={12} md={6} sx={{ pt: '24px !important', display: 'flex' }}>
                <Paper
                    elevation={0}
                    sx={{
                        pt: '7px',
                        pl: 3,
                        pr: 3,
                        pb: 3,
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        backgroundColor: '#ffffff',
                        width: '100%',
                        minHeight: '45vh',
                        maxHeight: '45vh',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center" mb="5px">
                        <Box
                            sx={{
                                width: 42,
                                height: 42,
                                borderRadius: '50%',
                                backgroundColor: (theme) =>
                                    alpha(theme.palette.warning.main, 0.12),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <UndoIcon sx={{ color: 'warning.main', fontSize: 24 }} />
                        </Box>
                        <Typography variant="h6" fontWeight={700}>
                            Devueltas
                        </Typography>
                    </Stack>
                    <Divider sx={{ mb: 2 }} />

                    <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                        {isAgendaLoading ? (
                            <Box display="flex" justifyContent="center" py={4}>
                                <CircularProgress size={32} />
                            </Box>
                        ) : !devueltas || devueltas.length === 0 ? (
                            <Typography color="text.secondary">
                                No se encontraron visitas devueltas
                            </Typography>
                        ) : (
                            renderList(
                                devueltas.map((v, i) => renderItem(v, i, false, false, 'ir', true))
                            )
                        )}
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
}
